import { useRouter } from 'next/router';
import React, { useEffect, useMemo, useRef, useState } from 'react';

interface Options {
  scrollHeight?: number;
  targetRef?: any;
  streamlinedHeader?: boolean;
  promoBannerRef?: React.MutableRefObject<HTMLDivElement>;
  headerSizesConfig?: any;
  skipCompletely?: boolean;
  skipShadowForRoute?: boolean;
  showCategoriesPastRandomScroll?: boolean;
  categoryRowDynamicScrollRef?: React.MutableRefObject<HTMLElement>;
  categoryRowShowsPastElementScrollRef?: React.MutableRefObject<HTMLElement>;
}

const boxShadowEffect = '0px 3px 8px rgba(0, 0, 0, 0.09)';
const boxShadowEffectCategoryL1 = '0px 7px 8px rgba(0, 0, 0, 0.09)';

/**
 * 页面滚动时候添加吸顶下部分阴影
 * @param ref 需要添加阴影的元素
 * @param options
 */
const useScrollToAddBoxShadow = (options?: Options) => {
  const eleRef = useRef(null);

  const { skipCompletely, skipShadowForRoute } = options || {};

  const [addBoxShadow, setAddBoxShare] = useState<boolean>(false);
  const [isScrolled, setIsScrolled] = useState<boolean>(false);
  const [hideCategoryRow, setHideCategoryRow] = useState<boolean>(true);

  let { headerNavHeight, isPromoBannerSticky } = options?.headerSizesConfig || {};
  const isStreamlinedHeader = options?.streamlinedHeader;
  const showCategoriesPastRandomScroll = isStreamlinedHeader && options?.showCategoriesPastRandomScroll === true;

  const handleScroll = () => {
    let scrollThreshold = options?.scrollHeight || 48;
    let addEffects: boolean;

    const pageContentEl = eleRef?.current;
    const top = pageContentEl?.getBoundingClientRect()?.top;
    let promoBannerHeight = 34;

    const promoBannerEl = options?.promoBannerRef?.current;
    const hasPromoBanner = promoBannerEl?.isConnected || false;

    if (isStreamlinedHeader) {
      if (hasPromoBanner && promoBannerEl) {
        const elHeight = promoBannerEl?.clientHeight;
        if (!!elHeight) {
          promoBannerHeight = elHeight;
        }
      }

      let isPromoBannerVisible;
      if (!hasPromoBanner) {
        isPromoBannerVisible = false;
      } else {
        isPromoBannerVisible = isPromoBannerSticky ? true : Math.ceil(top - headerNavHeight) >= 0;
      }
      if (pageContentEl?.previousElementSibling) {
        let stickyHeader = pageContentEl?.previousElementSibling;
        if (stickyHeader?.getAttribute('id') !== 'layout-header') {
          stickyHeader = document.querySelector('#layout-header');
        }
        const elHeight = stickyHeader?.clientHeight;
        if (!!elHeight) {
          headerNavHeight = elHeight;
        }
      }

      if (!hasPromoBanner) {
        addEffects = top < headerNavHeight;
      } else {
        if (isPromoBannerSticky) {
          if (hasPromoBanner && isPromoBannerSticky) {
            scrollThreshold = 1;
            addEffects = top >= scrollThreshold;
          }
          if (!showCategoriesPastRandomScroll && !hasPromoBanner && isPromoBannerSticky) {
            scrollThreshold = 1;
            addEffects = top >= scrollThreshold;
          } else {
            scrollThreshold = promoBannerHeight + headerNavHeight;
            addEffects = top <= scrollThreshold;
          }
        } else {
          if (!isPromoBannerVisible) {
            addEffects = true;
          } else {
            addEffects = false;
          }
        }
      }
    } else {
      addEffects = top <= scrollThreshold;
    }

    if (addEffects === true && document.documentElement?.scrollTop <= 1) {
      addEffects = false;
    }

    if (
      isStreamlinedHeader &&
      showCategoriesPastRandomScroll &&
      options?.categoryRowDynamicScrollRef?.current &&
      options?.categoryRowDynamicScrollRef?.current?.previousElementSibling &&
      options?.categoryRowShowsPastElementScrollRef?.current
    ) {
      if (addEffects == false) {
        setHideCategoryRow(true);
      } else {
        const elementBeforeCategoryBounding = options?.categoryRowDynamicScrollRef?.current?.previousElementSibling?.getBoundingClientRect();
        if (elementBeforeCategoryBounding) {
          const categoryTriggerBounding = options?.categoryRowShowsPastElementScrollRef?.current?.getBoundingClientRect();
          if (elementBeforeCategoryBounding && categoryTriggerBounding) {
            const categoryStartYAxis = elementBeforeCategoryBounding.height + elementBeforeCategoryBounding.y;
            const categoryTriggerBoundingYAxis = categoryTriggerBounding.y;
            const shouldBeVisible = categoryStartYAxis >= categoryTriggerBoundingYAxis;
            setHideCategoryRow(!shouldBeVisible);
          }
        }
      }
    }

    setAddBoxShare(addEffects);
    setIsScrolled(addEffects);
  };
  useEffect(() => {
    if (skipCompletely || skipShadowForRoute) return;

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  useEffect(() => {
    if (skipCompletely || skipShadowForRoute) return;

    let ref = options?.targetRef || eleRef;
    if (!ref.current) return;
    if (isStreamlinedHeader && hideCategoryRow === false) {
      ref.current.style.boxShadow = '';
    } else {
      ref.current.style.boxShadow = addBoxShadow ? boxShadowEffect : '';
    }
  }, [addBoxShadow]);

  useEffect(() => {
    if (!isStreamlinedHeader || !showCategoriesPastRandomScroll) return;

    const categoryRef = options?.categoryRowDynamicScrollRef?.current;
    if (!categoryRef) return;

    if (hideCategoryRow) {
      categoryRef.style.setProperty('opacity', '0');
      categoryRef.style.setProperty('pointer-events', 'none');
      categoryRef.style.setProperty('-webkit-box-shadow', 'unset');
      categoryRef.style.setProperty('-moz-box-shadow', 'unset');
      categoryRef.style.setProperty('box-shadow', 'unset');
    } else {
      categoryRef.style.setProperty('opacity', '1');
      categoryRef.style.setProperty('pointer-events', 'all');
      categoryRef.style.setProperty('-webkit-box-shadow', boxShadowEffectCategoryL1);
      categoryRef.style.setProperty('-moz-box-shadow', boxShadowEffectCategoryL1);
      categoryRef.style.setProperty('box-shadow', boxShadowEffectCategoryL1);
    }
  }, [hideCategoryRow]);

  return {
    eleRef,
    isScrolled
  };
};

export default useScrollToAddBoxShadow;
